/**
 * Iter 102.4 · Batch 4 — Reusable Administrative Action Dialog.
 *
 * Module-agnostic entry point for every future consumer of the
 * Administrative Actions Service. Attendance is Consumer #1 today;
 * Leave / Payroll / Claims / Assets will mount this same component
 * with different `module`, `actionTypes`, and `renderSubjectPicker`
 * props — no fork required.
 *
 * Governed by:
 *   - EDDR-003 v2 (Service positioning)
 *   - EDDR-004 (Attendance as Consumer #1)
 *   - AAS § 3 (dialog contract)
 *   - Batch-4 directive (Preview Impact panel before Confirm)
 *
 * Design notes:
 *   - Implementation fields (id, state, timestamps) are intentionally
 *     hidden from the operator.
 *   - Severity is READ-ONLY and derived from policy. The elevation
 *     switch is offered only when the current user holds the
 *     `<module>:admin:severity:override` permission (Super Admin
 *     today per EDDR-011 Q6).
 *   - Preview Impact panel calls a dry-run endpoint. Nothing is
 *     written until the operator hits "Confirm".
 *   - Evidence upload is a placeholder (Batch-4 scope defers actual
 *     Object Storage wiring — the file input is captured, filename
 *     stored, no upload yet; API accepts optional `evidence_filename`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const SEVERITY_STYLE = {
  low: "bg-slate-100 text-slate-700 border-slate-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  critical: "bg-rose-100 text-rose-800 border-rose-300",
};

const IMPACT_LABEL = {
  impact_attendance: "Attendance",
  impact_leave_ledger: "Leave Ledger",
  impact_payroll: "Payroll",
  impact_compliance: "Compliance",
  impact_analytics: "Analytics",
  impact_employee_timeline: "Employee Timeline",
  impact_reversible: "Reversible",
};

const APPROVER_LABEL = {
  hr_lead: "HR Lead",
  psa: "Primary Super Admin",
};

// Attendance-specific display map — extended per action_type when
// future consumers land. Kept small and legible so operators recognise
// the vocabulary at a glance.
const ACTION_TYPE_LABEL = {
  correction: "Correction",
  regularization: "Regularization",
  restoration: "Restoration",
  cancellation: "Cancellation",
  reopening: "Reopening",
  approval: "Administrative Approval",
};

export function AdministrativeActionDialog({
  open,
  onClose,
  onSubmitted,
  module,
  actionTypes = [], // ["correction", ...]
  policies = {}, // { correction: {default_severity, ...} }
  subjects = [], // [{id, label, secondary?}]
  submitEndpoint, // e.g. "/attendance/administrative-action"
  previewEndpoint, // e.g. "/attendance/administrative-action/preview"
  defaultSubjectId,
  defaultEffectiveDate,
  actionTypeLabel = (t) => ACTION_TYPE_LABEL[t] || t,
  // Iter 102.4 · Batch 4 polish — module-specific subject label so
  // "Subject Employee" reads simply "Employee" in the Attendance
  // context, "Claimant" for Claims, "Requester" for Leave, etc.
  subjectLabel = "Subject",
}) {
  const { can } = useAuth();

  const [actionType, setActionType] = useState(actionTypes[0] || "");
  const [subjectId, setSubjectId] = useState(defaultSubjectId || "");
  const [effectiveDate, setEffectiveDate] = useState(defaultEffectiveDate || "");
  const [reason, setReason] = useState("");
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showElevation, setShowElevation] = useState(false);
  const [severityOverride, setSeverityOverride] = useState("");

  const canElevate = can(`${module}:admin:severity:override`);

  // Reset form on open/close.
  useEffect(() => {
    if (open) {
      setActionType(actionTypes[0] || "");
      setSubjectId(defaultSubjectId || "");
      setEffectiveDate(defaultEffectiveDate || "");
      setReason("");
      setEvidenceFile(null);
      setPreview(null);
      setPreviewError("");
      setShowElevation(false);
      setSeverityOverride("");
    }
  }, [open, actionTypes, defaultSubjectId, defaultEffectiveDate]);

  const currentSubject = useMemo(
    () => subjects.find((s) => s.id === subjectId),
    [subjects, subjectId],
  );

  // Dry-run preview whenever the derivation inputs change.
  const refreshPreview = useCallback(async () => {
    if (!actionType || !subjectId || !effectiveDate) {
      setPreview(null);
      setPreviewError("");
      return;
    }
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const body = {
        action_type: actionType,
        subject_user_id: subjectId,
        effective_date: effectiveDate,
      };
      if (severityOverride) body.severity_override = severityOverride;
      const { data } = await api.post(previewEndpoint, body);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setPreviewError(formatApiError(err, "Could not preview impact"));
    } finally {
      setPreviewLoading(false);
    }
  }, [actionType, subjectId, effectiveDate, severityOverride, previewEndpoint]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(refreshPreview, 200); // debounce
    return () => clearTimeout(t);
  }, [open, refreshPreview]);

  const handleSubmit = async () => {
    if (!actionType || !subjectId || !effectiveDate) {
      toast.error("Action Type, Subject, and Effective Date are required.");
      return;
    }
    if ((reason || "").trim().length < 10) {
      toast.error("Reason must be at least 10 characters (audit requirement).");
      return;
    }
    if (preview?.requires_evidence && !evidenceFile) {
      toast.error("Evidence is required for this action per policy.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        action_type: actionType,
        subject_user_id: subjectId,
        effective_date: effectiveDate,
        reason: reason.trim(),
        new_state: {},
      };
      if (severityOverride) body.severity_override = severityOverride;
      if (evidenceFile) body.evidence_filename = evidenceFile.name;
      const { data } = await api.post(submitEndpoint, body);
      if (data.pending_approval) {
        toast.success("Administrative action submitted for approval.");
      } else {
        toast.success("Administrative action recorded.");
      }
      onSubmitted?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err, "Administrative action failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const previewSummary = preview ? (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 space-y-3"
      data-testid="aa-preview-panel"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Preview Impact
          </div>
          <div className="text-sm font-semibold text-slate-800">
            Administrative Action · {actionTypeLabel(preview.action_type)}
          </div>
        </div>
        <Badge
          className={`border ${SEVERITY_STYLE[preview.severity]} font-semibold`}
          data-testid="aa-preview-severity"
        >
          Severity: {preview.severity.toUpperCase()}
          {preview.severity_source === "override" && (
            <span className="ml-2 text-[9px] uppercase tracking-wide">
              · Elevated
            </span>
          )}
        </Badge>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-xs">
        {currentSubject && (
          <div>
            <dt className="text-slate-500">Subject</dt>
            <dd className="text-slate-800 font-medium" data-testid="aa-preview-subject">
              {currentSubject.label}
              {currentSubject.secondary && (
                <span className="text-slate-400 ml-1 font-mono">
                  · {currentSubject.secondary}
                </span>
              )}
            </dd>
          </div>
        )}
        {effectiveDate && (
          <div>
            <dt className="text-slate-500">Effective Date</dt>
            <dd className="text-slate-800 font-medium" data-testid="aa-preview-date">
              {effectiveDate}
            </dd>
          </div>
        )}

        <div className="sm:col-span-2">
          <dt className="text-slate-500 mb-1">Business Impact</dt>
          <dd className="flex flex-wrap gap-1.5" data-testid="aa-preview-impact-flags">
            {preview.impact_flags.map((f) => (
              <Badge
                key={f}
                variant="outline"
                className="text-[10px] bg-white border-slate-300"
              >
                <span className="text-emerald-600 mr-1">✔</span>
                {IMPACT_LABEL[f] || f}
              </Badge>
            ))}
          </dd>
        </div>

        <div className="sm:col-span-2">
          <dt className="text-slate-500 mb-1">Approval</dt>
          <dd className="text-slate-800 font-medium" data-testid="aa-preview-approval">
            {preview.requires_approval ? (
              <>
                <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px]">
                  Required
                </Badge>
                <span className="ml-2 text-slate-600 text-xs">
                  {preview.approvers
                    .map((r) => APPROVER_LABEL[r] || r)
                    .join(" + ")}
                </span>
              </>
            ) : (
              <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]">
                Not Required
              </Badge>
            )}
          </dd>
        </div>

        <div className="sm:col-span-2">
          <dt className="text-slate-500 mb-1">Payroll</dt>
          <dd data-testid="aa-preview-payroll">
            {preview.payroll_adjustment_required ? (
              <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2">
                <span className="text-rose-600 text-base leading-none pt-0.5">⚠</span>
                <div>
                  <div className="text-rose-800 font-semibold text-xs">
                    Frozen period — payroll recompute required.
                  </div>
                  <div className="text-rose-700 text-[11px] mt-0.5">
                    The payroll submit-gate will flag this action until a Re-Freeze
                    + Recompute is run OR the action is reversed.
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-slate-700 text-xs">
                No recompute required.
              </div>
            )}
          </dd>
        </div>

        {preview.requires_evidence && (
          <div className="sm:col-span-2">
            <dt className="text-slate-500 mb-1">Evidence</dt>
            <dd className="text-rose-700 text-xs" data-testid="aa-preview-evidence-req">
              Evidence upload is required for this action per policy.
            </dd>
          </div>
        )}
      </dl>
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="administrative-action-dialog"
      >
        <DialogHeader>
          <DialogTitle>Administrative Action</DialogTitle>
          <DialogDescription>
            Override the standard workflow with an auditable administrative
            action. Every write is immutable and appears on the operator's
            timeline. Review the Preview Impact panel below before confirming.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Action Type */}
          <div>
            <Label className="text-xs">Action Type</Label>
            <select
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              data-testid="aa-action-type"
            >
              {actionTypes.map((t) => (
                <option key={t} value={t}>
                  {actionTypeLabel(t)}
                  {policies[t]?.default_severity
                    ? ` · policy severity: ${policies[t].default_severity}`
                    : ""}
                </option>
              ))}
            </select>
            <div
              className="text-[11px] text-slate-500 mt-1"
              data-testid="aa-severity-helper"
            >
              Severity is derived from policy. Elevation is restricted to
              authorised users.
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs">{subjectLabel}</Label>
            <select
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              data-testid="aa-subject"
            >
              <option value="">— select —</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                  {s.secondary ? ` · ${s.secondary}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Effective Date */}
          <div>
            <Label className="text-xs">Effective Date</Label>
            <Input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              data-testid="aa-effective-date"
            />
          </div>

          {/* Reason */}
          <div>
            <Label className="text-xs">
              Reason (mandatory · ≥ 10 chars · appears in audit trail)
            </Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Biometric device offline on 14-Jul. Verified via CCTV + Slack timestamp."
              data-testid="aa-reason"
            />
          </div>

          {/* Evidence placeholder */}
          <div>
            <Label className="text-xs">
              Evidence (optional · required for high-blast-radius actions)
            </Label>
            <Input
              type="file"
              onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
              data-testid="aa-evidence"
            />
            {evidenceFile && (
              <div className="text-[10px] text-slate-500 mt-1 font-mono">
                {evidenceFile.name} · {(evidenceFile.size / 1024).toFixed(1)} KB
              </div>
            )}
            <div className="text-[10px] text-slate-400 mt-1">
              File is captured for audit; full object-storage upload lands in a
              follow-up iteration.
            </div>
          </div>

          {/* Severity elevation (super-admin only) */}
          {canElevate && (
            <div className="pt-1 border-t border-slate-100">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={showElevation}
                  onChange={(e) => {
                    setShowElevation(e.target.checked);
                    if (!e.target.checked) setSeverityOverride("");
                  }}
                  data-testid="aa-elevate-toggle"
                />
                Elevate severity (Super Admin only)
              </label>
              {showElevation && (
                <select
                  className="mt-2 h-9 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                  value={severityOverride}
                  onChange={(e) => setSeverityOverride(e.target.value)}
                  data-testid="aa-severity-override"
                >
                  <option value="">— keep policy severity —</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              )}
            </div>
          )}

          {/* Preview Impact */}
          <div className="pt-3">
            {previewLoading && (
              <div className="text-xs text-slate-400" data-testid="aa-preview-loading">
                Resolving impact…
              </div>
            )}
            {previewError && (
              <div className="text-xs text-rose-600" data-testid="aa-preview-error">
                {previewError}
              </div>
            )}
            {previewSummary}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="aa-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || previewLoading || !preview || !!previewError}
            data-testid="aa-confirm"
          >
            {submitting
              ? "Submitting…"
              : previewLoading
              ? "Resolving impact…"
              : preview?.requires_approval
              ? "Submit for Approval"
              : "Confirm & Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdministrativeActionDialog;
